import React, { useCallback } from 'react';
import { styled, css } from 'styled-components';

import { Badge } from 'components/bootstrap';
import type { ColumnRenderers } from 'components/common/EntityDataTable';
import { Timestamp } from 'components/common';
import Routes from 'routing/Routes';
import { Link } from 'components/common/router';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useLocation from 'routing/useLocation';
import { getPathnameWithoutId } from 'util/URLUtils';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';

import type { DataLakeRetrievalResult, RestoreIndex } from '../useDataLakeRetrievals';

const RetrievedStatusBadge = styled(Badge)(
  ({ theme }) => css`
    margin-left: ${theme.spacings.xxs};
  `,
);

type StreamTitleCellProps = {
  streamId: string;
  streamTitle: string;
};

const StreamTitleCell = ({ streamId, streamTitle }: StreamTitleCellProps) => {
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();

  const handleClick = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.RETRIEVALS.SHOW_MESSAGES_BUTTON_CLICKED, {
      app_pathname: getPathnameWithoutId(pathname),
    });
  }, [pathname, sendTelemetry]);

  return (
    <Link to={`${Routes.stream_view(streamId)}?segment=destinations`} onClick={handleClick}>
      {streamTitle}
    </Link>
  );
};

const customColumnRenderers: ColumnRenderers<DataLakeRetrievalResult> = {
  attributes: {
    retrieved_at: {
      renderCell: (retrieved_at: string) => <Timestamp dateTime={retrieved_at} />,
    },
    status: {
      renderCell: (status: string) => (
        <RetrievedStatusBadge bsStyle={status === 'COMPLETE' ? 'success' : 'warning'}>
          {status}
        </RetrievedStatusBadge>
      ),
    },
    timestamp_from: {
      renderCell: (timestamp_from: string) => <Timestamp dateTime={timestamp_from} />,
    },
    timestamp_to: {
      renderCell: (timestamp_to: string) => <Timestamp dateTime={timestamp_to} />,
    },
    stream_title: {
      renderCell: (stream_title: string, entity: DataLakeRetrievalResult) => (
        <StreamTitleCell streamId={entity.stream_id} streamTitle={stream_title} />
      ),
    },
    restore_indices: {
      renderCell: (restore_indices: RestoreIndex[]) => <>{restore_indices.length}</>,
    },
    retrieved_documents: {
      staticWidth: 200,
    },
  },
};

export default customColumnRenderers;
